/**
 * FilterPills — the multi-select filter-pill row used across admin list
 * surfaces (Partners queue, User Management). Each pill is a toggle into a
 * `Set` of selected keys, shows a live count, and the row carries Select-All /
 * Deselect-All. Deselecting all is a valid state — the caller renders an
 * empty-list hint (the pills don't silently fall back to "show everything").
 *
 * Documented in `Docs/Technical/portal-staff-component-library.md`.
 */
export interface FilterPillGroup<K extends string = string> {
  key: K;
  label: string;
  icon?: string;
  count: number;
  /** Hidden from non-super_admin callers (e.g. the Partners "Deleted" pill). */
  superAdminOnly?: boolean;
}

export interface FilterPillsProps<K extends string = string> {
  groups: FilterPillGroup<K>[];
  selected: Set<K>;
  onChange: (next: Set<K>) => void;
  isSuperAdmin?: boolean;
  ariaLabel?: string;
}

export function FilterPills<K extends string>({
  groups,
  selected,
  onChange,
  isSuperAdmin = true,
  ariaLabel = 'Filter',
}: FilterPillsProps<K>) {
  const visible = groups.filter((g) => !g.superAdminOnly || isSuperAdmin);
  const toggle = (key: K) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };
  return (
    <div className="portal-filter-pills" role="group" aria-label={ariaLabel}>
      {visible.map((g) => {
        const active = selected.has(g.key);
        return (
          <button
            key={g.key}
            type="button"
            className={`portal-filter-pill portal-filter-pill-${g.key}${active ? ' portal-filter-pill-selected' : ''}`}
            onClick={() => toggle(g.key)}
            aria-pressed={active}
            title={active ? `Hide ${g.label}` : `Show ${g.label}`}
          >
            {g.icon && <span className="portal-filter-pill-icon" aria-hidden="true">{g.icon}</span>}
            <span className="portal-filter-pill-label">{g.label}</span>
            <span className="portal-filter-pill-count">{g.count}</span>
          </button>
        );
      })}
      <button
        type="button"
        className="portal-filter-pill-clear"
        onClick={() => onChange(new Set(visible.map((g) => g.key)))}
        title="Select every filter pill"
      >
        Select All
      </button>
      <button
        type="button"
        className="portal-filter-pill-clear"
        onClick={() => onChange(new Set())}
        title="Clear all selected filter pills (the list shows nothing until you click a pill)"
      >
        Deselect All
      </button>
    </div>
  );
}
